import { useMemo } from "react";
import { DataTable, DataTableColumnDef } from "@shared/EDS/components/DataTable/DataTable";
import { DateTime } from "luxon";
import { useFilterState } from "@shared/common/hooks/useFilterState";
import { ServiceTask } from "@opstel/query/v2/models/serviceTask";
import { FullPageWithPaddingContainer } from "@shared/EDS/components/FullPageWithPaddingContainer";
import { Toolbar } from "@shared/EDS/components/Toolbar";
import { DATETIME_FORMAT_NO_DATE } from "@shared/common/utils/luxonHelpers";
import { TractieSearchInput } from "@tractie/components";
import { HighlightedText } from "@shared/common/components/HighlightedText";
import { DateRadioPanel } from "@shared/common/components/DateRadioPanel";

export interface ServiceTasksProps {
  readonly data: ServiceTask[];
}

function getColumns(trainNumberSearchString: string): DataTableColumnDef<ServiceTask>[] {
  return [
    {
      id: "type",
      header: "Servicetaak",
      accessorKey: "type",
      enableSorting: true,
    },
    {
      id: "startTime",
      header: "Starttijd",
      accessorFn: (serviceTask) => serviceTask.timeInterval.start,
      cell: ({ row }) => row.original.timeInterval.start.toFormat(DATETIME_FORMAT_NO_DATE),
      enableSorting: true,
      filterFn: (row, _columnId, filterValue?: DateTime) => {
        if (!filterValue) return true;
        return row.original.timeInterval.start.hasSame(filterValue, "day");
      },
      sortFn: (rowA, rowB) =>
        rowA.original.timeInterval.start.toSeconds() - rowB.original.timeInterval.start.toSeconds(),
    },
    {
      id: "endTime",
      header: "Eindtijd",
      accessorFn: (serviceTask) => serviceTask.timeInterval.end,
      cell: ({ row }) => row.original.timeInterval.end.toFormat(DATETIME_FORMAT_NO_DATE),
      enableSorting: true,
      sortFn: (rowA, rowB) =>
        rowA.original.timeInterval.end.toSeconds() - rowB.original.timeInterval.end.toSeconds(),
    },
    {
      id: "track",
      header: "Spoor",
      accessorKey: "trackName",
      enableSorting: true,
    },
    {
      id: "arrivingTrainNumber",
      header: "Aankomst treinnr.",
      accessorKey: "arrivingTrainNumber",
      cell: ({ row }) => (
        <HighlightedText
          text={row.original.arrivingTrainNumber}
          highlightedText={trainNumberSearchString}
        />
      ),
      enableSorting: true,
      filterFn: (row, _columnId, filterValue: string) => {
        if (filterValue === "") return true;
        return (
          row.original.arrivingTrainNumber.includes(filterValue) ||
          row.original.departingTrainNumber.includes(filterValue)
        );
      },
    },
    {
      id: "departingTrainNumber",
      header: "Vertrek treinnr.",
      accessorKey: "departingTrainNumber",
      cell: ({ row }) => (
        <HighlightedText
          text={row.original.departingTrainNumber}
          highlightedText={trainNumberSearchString}
        />
      ),
      enableSorting: true,
    },
  ];
}

export function ServiceTasks({ data }: ServiceTasksProps) {
  const [currentDateFilter, setDateFilter] = useFilterState<DateTime | undefined>({
    id: "startTime",
    value: undefined,
  });
  const [searchInput, setSearchInput] = useFilterState({ id: "arrivingTrainNumber", value: "" });
  const columns = useMemo(() => getColumns(searchInput.value), [searchInput]);

  return (
    <FullPageWithPaddingContainer>
      <div className="flex items-center gap-xs">
        <h3 className="m-0 font-medium">Servicetaken</h3>
        <tractie-tag color="blue" appearance="subtle" label={data.length.toString()} size="m" />
      </div>
      <Toolbar>
        <div className="flex w-full items-center justify-between">
          <DateRadioPanel
            dateTimes={data.map((d) => d.timeInterval.start)}
            onChange={setDateFilter}
          />
          <tractie-search-input
            className="min-w-70"
            placeholder="Zoeken op treinnummer"
            modelValue={searchInput.value}
            onmodel-value-changed={(e) =>
              setSearchInput((e.target as TractieSearchInput).value ?? "")
            }
          />
        </div>
      </Toolbar>
      <DataTable
        data-testid="service-tasks-table"
        columns={columns}
        data={data}
        defaultSort={[
          { id: "startTime", desc: false },
          { id: "endTime", desc: false },
        ]}
        filters={[searchInput, currentDateFilter]}
        options={{
          emptyMessage: "Er zijn geen resultaten beschikbaar.",
        }}
      />
    </FullPageWithPaddingContainer>
  );
}
